import { useRef } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { updateSoilSightScenario } from "services";

import { useIsAuthenticated } from "utils";

import { GET_SOIL_SIGHT_SCENARIO, LIST_SOIL_SIGHT_SCENARIOS, UPDATE_SOIL_SIGHT_SCENARIO } from "consts";

import { SoilSightScenario, SoilSightScenarioDetail, UpdateSoilSightScenarioBody } from "models";

type MutateArgs = {
  scenarioId: string;
  body: UpdateSoilSightScenarioBody;
  requestVersion?: number;
};

export const useUpdateSoilSightScenario = () => {
  const queryClient = useQueryClient();
  const isAuthenticated = useIsAuthenticated();
  const latestAppliedVersionRef = useRef<Map<string, number>>(new Map());

  return useMutation({
    // Per-scenario serialization happens via [UPDATE_SOIL_SIGHT_SCENARIO,
    // scenarioId] in the variables; useIsMutating picks them up by prefix.
    mutationKey: [UPDATE_SOIL_SIGHT_SCENARIO],
    mutationFn: ({ scenarioId, body }: MutateArgs) => {
      if (!isAuthenticated) return Promise.reject(new Error("Not Authorized"));
      return updateSoilSightScenario(scenarioId, body);
    },
    // Patch the detail and list caches in place rather than invalidating, so
    // active subscribers don't refetch + remount during edits
    onSuccess: (_data, vars) => {
      if (vars.requestVersion !== undefined) {
        const latestApplied = latestAppliedVersionRef.current.get(vars.scenarioId) ?? 0;
        if (vars.requestVersion < latestApplied) return;
        latestAppliedVersionRef.current.set(vars.scenarioId, vars.requestVersion);
      }

      queryClient.setQueryData<SoilSightScenarioDetail | undefined>([GET_SOIL_SIGHT_SCENARIO, vars.scenarioId], (old) =>
        old ? { ...old, ...vars.body } : old
      );
      queryClient.setQueriesData<SoilSightScenario[] | undefined>({ queryKey: [LIST_SOIL_SIGHT_SCENARIOS] }, (old) =>
        old ? old.map((s) => (s.id === vars.scenarioId ? { ...s, ...(vars.body.name == null ? {} : { name: vars.body.name }) } : s)) : old
      );
    },
    // The page copies unsaved edits into the detail cache (syncScenarioDetailCache); mark it stale so it is refetched before reuse.
    onError: (_error, vars) => {
      queryClient.invalidateQueries({ queryKey: [GET_SOIL_SIGHT_SCENARIO, vars.scenarioId], refetchType: "none" });
    },
  });
};
